<template>
    <div class="game-wrapper">
        <register-user-modal :show="registerUser" :gameUsers="gameUsers" @close="closeRegister"></register-user-modal>
        <wheel-select-modal v-if="playingGame" :show="suggestWheel" :users="playingGame.users" @close="suggestWheel = false"></wheel-select-modal>
        <fade-transition>
            <dashboard
                v-if="view === 'play-game' && playingGame" 
                :game="playingGame" 
                :users="users"
                :hasChanges="hasChanges" 
                @updateGame="handleUpdateGame" 
                @userOrderUpdated="handleUserOrderUpdated" 
                @back="handleBack"
            ></dashboard>
            <select-game v-else-if="view === 'select-game'" :games="games" @selectGame="handleSelectGame" @startNewGame="handleStartNewGame"></select-game>
            <create-game v-else-if="view === 'create-game'" :users="users" @gameCreated="handleGameCreated"></create-game>
        </fade-transition>
    </div>
</template>

<script>
import { httpRequest } from '@/utils.js';

import CreateGame from './CreateGame.vue';
import SelectGame from './SelectGame.vue';
import Dashboard from './Dashboard.vue';
import RegisterUserModal from './RegisterUserModal.vue';
import WheelSelectModal from './WheelSelectModal.vue';

import FadeTransition from '@/components/transitions/FadeTransition.vue';

export default {
    name: 'play-game',
    components: {
        CreateGame,
        SelectGame,
        Dashboard,
        RegisterUserModal,
        WheelSelectModal,
        FadeTransition
    },
    props: {
        socket: {
            type: Object,
            required: true
        },
        users: {
            type: Array,
            required: true
        },
        games: {
            type: Array,
            required: true
        },
        view: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            isSelectingGame: false,
            isCreatingNewGame: false,
            playingGame: null,
            registerUser: false,
            hasChanges: false,
            suggestWheel: false,
            wakeLockActive: false,
            gameUsers: []
        }
    },
    methods: {
        handleStartNewGame() {
            this.isSelectingGame = false;
            this.isCreatingNewGame = true;
            this.$emit('updateView', 'create-game');
        },
        handleGameCreated(game) {
            this.$emit('gameCreated', game);
            this.suggestWheel = true;
            this.handleSelectGame(game.id);
        },
        async handleSelectGame(id) {
            const currentGame = this.games.find(game => game.id === id);
            if(!currentGame) return;

            this.socket.emit('playGame', id);

            this.setPlayingGame(currentGame);
            this.$emit('updateView', 'play-game');
            
            this.$router.push({
                path: `/${currentGame.id}`,
                query: this.$route.query
            }).catch(error => {});
        },
        async handleUpdateGame(game) {
            console.log('HANDLE UPDATE GAME')
            this.hasChanges = true;
            const parsedRounds = _.cloneDeep(game.rounds).map(round => {
                delete round.users;
                return round;
            });
            const updatedGame = await httpRequest('POST', `/games/${game.id}`, { rounds: parsedRounds });
            this.setPlayingGame(updatedGame);
            this.hasChanges = false;
        },
        async handleUserOrderUpdated(userIds) {
            httpRequest('POST', `/games/${this.playingGame.id}/users_order`, { userIds });
        },
        async initializeUserSession() {
            const you = localStorage.getItem('you');
            if (you || !this.playingGame) return;

            const hasUnregisteredGameUsers = this.users.some(user => this.playingGame.userIds.includes(user.id) && !user.subscription);
            if (!you && this.playingGame && hasUnregisteredGameUsers) return this.registerUser = true;
        },
        initializeNotificationRedirect() {
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.addEventListener('message', event => {
                    if (event.data.type === 'MOVE_TO_GAME') {
                        const currentUrl = event.data.url;
                        
                        if ((currentUrl.endsWith('/') || currentUrl.endsWith('/create')) && event.data.moveToUrl) {
                            this.$router.push('/'+event.data.moveToUrl.split('/').pop());
                        }
                    }
                });
            }
        },
        async clearAllNotifications() {
            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.ready;
                const notifications = await registration.getNotifications();
                
                notifications.forEach(notification => {
                    notification.close();
                });

                console.log(`[PWA] ${notifications.length} notificaties verwijderd.`);
            }
        },
        closeRegister(user) {
            this.registerUser = false;
            if(user) this.$emit('register', user);
        },
        handleBack() {
            this.playingGame = null;
            localStorage.removeItem('playingGame');
            this.releaseWakeLock();
            if(this.games.length > 0) this.$emit('updateView', 'select-game');
            else this.$emit('updateView', 'create-game');
        },
        setPlayingGame(game) {
            console.log('setplayinggame')
            game.rounds = game.rounds.map(round => {
                round.users = round.userIds.map((userId, index) => {
                    const user = game.users.find(user => user.id === userId);
                    const { base64avatar, keyId } = this.users.find(user => user.id === userId) || {};
                    user.base64avatar = base64avatar;
                    user.index = index;
                    user.keyId = keyId;
                    return user
                });
                return round
            });
            const lastRound = game.rounds[game.rounds.length-1];
            game.users = game.users.map(gameUser => {
                const index = lastRound.userIds.findIndex(userId => gameUser.id === userId);
                gameUser.index = index;
                const { base64avatar } = this.users.find(user => user.id === gameUser.id) || {};
                gameUser.base64avatar = base64avatar;
                return gameUser
            });
            this.playingGame = _.cloneDeep(game);
            this.setLocalStoragePlayingGame();
            this.gameUsers = game.userIds.map(userId => this.users.find(user => user.id === userId));

            this.initializeUserSession();
        },
        setLocalStoragePlayingGame() {
            const game = _.cloneDeep(this.playingGame);
            delete game.users;
            game.rounds = game.rounds.map(round => {
                delete round.users;
                return round;
            });
            localStorage.setItem('playingGame', JSON.stringify(game));
        },
        updatePlayingGame(game) {
            const updatedGame = this.playingGame;
            updatedGame.rounds = game.rounds.map(round => {
                round.users = round.userIds.map((userId, index) => {
                    const user = game.users.find(user => user.id === userId);
                    const { base64avatar, keyId, name } = this.users.find(user => user.id === userId) || {};
                    user.base64avatar = base64avatar;
                    user.name = name;
                    user.index = index;
                    user.keyId = keyId;
                    return user
                });
                return round
            });
            const lastRound = game.rounds[game.rounds.length-1];
            updatedGame.users = game.users.map(user => {
                const index = lastRound.userIds.findIndex(userId => user.id === userId);
                const { base64avatar, keyId } = this.users.find(appUser => appUser.id === user.id) || {};
                user.base64avatar = base64avatar;
                user.index = index;
                user.keyId = keyId;
                return user
            });
            this.playingGame = _.cloneDeep(updatedGame);
        },
        async handleWindowFocus() {
            this.clearAllNotifications();

            if (!this.playingGame) return;
            
            await this.$parent.refresh();

            const game = this.games.find(game => game.id === this.playingGame.id);
            console.log('window focused, game:',game)
            this.updatePlayingGame(game);
        },
    },
    created() {
        this.initializeUserSession();
        this.initializeNotificationRedirect();
        
        const gameId = this.$route.params.id;
        const matchingGame = this.games.find(game => game.id === gameId);
        if (matchingGame) this.handleSelectGame(matchingGame.id);

        if(!this.playingGame) {
            if(this.games.length === 0) this.isCreatingNewGame = true;
            else this.isSelectingGame = true;
        }

        this.$root.$on('windowfocus', this.handleWindowFocus);

        this.socket.on('game_updated', (game) => {
            if (this.hasChanges) return

            this.playingGame.rounds = _.cloneDeep(game.rounds);
            this.playingGame.users = game.userIds.map((userId, index) => {
                const gameUser = this.playingGame.users.find(user => user.id === userId);
                const localUser = this.users.find(user => user.id === userId);
                gameUser.base64avatar = localUser.base64avatar;
                gameUser.index = index;
                return gameUser
            });
        });

        this.socket.on('game_users_updated', (game) => {
            this.updatePlayingGame(game);
        });

        this.socket.on('user_profile_updated', ({ id, name }) => {
            if (!this.playingGame) return;
            
            this.playingGame.users = this.playingGame.users.map(user => {
                if (user.id === id) user.name = name;
                return user;
            });
        });
    },
    beforeDestroy() {
        this.$root.$off('windowfocus', this.handleWindowFocus);
    },
    watch: {
        view: function() {
            if(this.view !== 'play-game') {
                this.registerUser = false;
                this.playingGame = null;
                localStorage.removeItem('playingGame');
                return
            }
            
            this.initializeUserSession();
        },
        users: {
            handler() {
                if (!this.playingGame) return;
                
                this.playingGame.users = this.playingGame.users.map(gameUser => {
                    const { base64avatar } = this.users.find(user => user.id === gameUser.id) || {};
                    gameUser.base64avatar = base64avatar;
                    gameUser.hasAvatar = base64avatar;
                    return gameUser
                });
            },
            deep: true
        },
    }
}
</script>

<style lang="scss" scoped>

.game-wrapper {
    width: 100%;
}

</style>